import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { announcementsAPI } from '../../services/api';
import { ArrowLeft, Calendar, FileText } from 'lucide-react';

const IssuerAnnouncementDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewUrls, setPreviewUrls] = useState({}); // fileId -> objectURL
  const SHOW_INLINE_PREVIEWS = true; // render images/PDFs inline using PB file URL

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const resp = await announcementsAPI.getAnnouncement(id);
        const ann = resp?.data?.data?.announcement || resp?.data?.announcement || null;
        if (mounted) setData(ann);
      } catch (e) {
        if (mounted) setError('Announcement not found');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  // Build previews for attachments missing URL by fetching via backend proxy with auth (fileId only; no pbRecordId fallback)
  useEffect(() => {
    let revoked = [];
    const buildPreviews = async () => {
      if (!data || !Array.isArray(data.attachments)) return;
      const tasks = data.attachments
        .filter(a => !a?.url && !a?.fileUrl)
        .map(async (a) => {
          const fid = a.fileId || a.id;
          const key = fid;
          if (!key) return;
          try {
            const resp = await api.get(`/api/files/${fid}/content`, { responseType: 'blob' });
            if (resp && resp.data) {
              const blobUrl = URL.createObjectURL(resp.data);
              revoked.push(blobUrl);
              setPreviewUrls(prev => ({ ...prev, [key]: blobUrl }));
            }
          } catch (e) {
            // ignore; keep as not available
          }
        });
      await Promise.allSettled(tasks);
    };
    buildPreviews();
    return () => {
      revoked.forEach(u => URL.revokeObjectURL(u));
    };
  }, [data]);

  if (loading) return <div className="p-6">Loading…</div>;
  if (error || !data) return <div className="p-6 text-error-600">{error || 'Announcement not found'}</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </button>

          <div className="card">
            <div className="card-header">
              <h1 className="text-2xl font-bold text-gray-900">{data.title}</h1>
              <div className="mt-2 flex items-center gap-4 text-sm text-secondary-600">
                <span className="inline-flex items-center"><FileText className="w-4 h-4 mr-1" />{data.bondId?.bondName || '—'}</span>
                <span className="inline-flex items-center"><Calendar className="w-4 h-4 mr-1" />{new Date(data.publishedAt || data.createdAt).toLocaleString()}</span>
              </div>
            </div>
            <div className="card-body">
              <div className="prose max-w-none whitespace-pre-wrap text-dark-gray">{data.content}</div>

              {Array.isArray(data.attachments) && data.attachments.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-dark-gray mb-2">Attachments</h3>
                  <div className="space-y-4">
                    {data.attachments.map((f, idx) => {
                      const fileId = f?.fileId || f?.id;
                      const key = fileId;
                      const url = f?.url || f?.fileUrl || (key ? previewUrls[key] : undefined);
                      const name = f?.fileName || f?.filename || f?.originalName || (url ? new URL(url).pathname.split('/').pop() : 'Attachment');
                      const mime = (f?.mimeType || '').toLowerCase();
                      const ext = (name || '').toLowerCase().split('.').pop();
                      const isImage = mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'tiff', 'tif', 'heif', 'heic', 'avif'].includes(ext);
                      const isPdf = mime === 'application/pdf' || ext === 'pdf';
                      return (
                        <div key={idx} className="border rounded-md overflow-hidden">
                          {url && isImage && SHOW_INLINE_PREVIEWS ? (
                            <div className="bg-secondary-50 p-3">
                              <img src={url} alt={name} className="max-h-96 w-auto rounded shadow-sm" />
                              <div className="mt-2 text-sm text-secondary-700 break-all">{name}</div>
                            </div>
                          ) : url && isPdf && SHOW_INLINE_PREVIEWS ? (
                            <div className="bg-secondary-50">
                              <iframe title={`pdf-${idx}`} src={url} className="w-full h-[500px]" />
                              <div className="p-2 text-sm">
                                <a href={url} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">Open PDF in new tab</a>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between p-3 bg-secondary-50">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className="w-4 h-4 text-secondary-400" />
                                <span className="text-sm text-secondary-700 truncate" title={name}>{name}</span>
                              </div>
                              {url ? (
                                <div className="flex items-center gap-3">
                                  <a href={url} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">View</a>
                                  <a href={url} download className="text-secondary-600 hover:underline">Download</a>
                                </div>
                              ) : (
                                <span className="text-xs text-secondary-500">Not available</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssuerAnnouncementDetails;
